// Generates a story-sized share card (1080x1920) for Spark answers.
// Prompt is always included. Answer is optional (opt-in).

export async function generateSparkShareCard({
  categoryLabel = "Spark",
  prompt = "",
  answer = "",
  includeAnswer = false,
  brand = "UnityLOVE",
  subtitle = "Connection Arcade",
} = {}) {
  const W = 1080;
  const H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#050816");     // deep navy
  g.addColorStop(0.55, "#0b1220");  // slate
  g.addColorStop(1, "#120a2a");     // violet hint
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Subtle stars
  ctx.globalAlpha = 0.14;
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * W;
    const y = Math.random() * H;
    const r = Math.random() * 2.2;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  const pad = 72;
  const cardX = pad;
  const cardY = 220;
  const cardW = W - pad * 2;
  const cardH = H - 420;

  // Card container
  roundRect(ctx, cardX, cardY, cardW, cardH, 42);
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Header
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = "800 52px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText(brand, pad, 120);

  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = "600 28px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText(subtitle, pad, 165);

  // Category pill
  const pillText = categoryLabel || "Spark";
  const pillW = measurePill(ctx, pillText, 26, 18, 34);
  const pillX = cardX + 36;
  const pillY = cardY + 36;
  roundRect(ctx, pillX, pillY, pillW, 54, 27);
  ctx.fillStyle = "rgba(139,92,246,0.25)";
  ctx.fill();
  ctx.strokeStyle = "rgba(139,92,246,0.45)";
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = "800 26px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText(pillText, pillX + 18, pillY + 36);

  // Prompt
  const contentX = cardX + 36;
  let y = pillY + 110;
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = "900 22px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText("PROMPT", contentX, y);
  y += 26;

  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.font = "800 42px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  y = drawWrappedText(ctx, prompt || "", contentX, y + 18, cardW - 72, 52, 10);

  // Divider
  y += 24;
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(contentX, y);
  ctx.lineTo(contentX + cardW - 72, y);
  ctx.stroke();
  y += 38;

  // Answer (optional)
  if (includeAnswer) {
    ctx.fillStyle = "rgba(255,255,255,0.72)";
    ctx.font = "900 22px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    ctx.fillText("MY ANSWER", contentX, y);
    y += 26;
    ctx.fillStyle = "rgba(255,255,255,0.90)";
    ctx.font = "700 34px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    drawWrappedText(ctx, answer || "", contentX, y + 16, cardW - 72, 44, 14);
  } else {
    ctx.fillStyle = "rgba(255,255,255,0.70)";
    ctx.font = "700 28px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    drawWrappedText(ctx, "Want to answer this Spark?", contentX, y + 16, cardW - 72, 40, 2);
  }

  // Footer CTA
  const footerY = H - 150;
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.font = "800 28px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText("Play in UnityLOVE \u2192", pad, footerY);

  ctx.fillStyle = "rgba(255,255,255,0.60)";
  ctx.font = "600 24px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.fillText("Connection Arcade", pad, footerY + 40);

  // Export to blob
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
  return blob;
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 10) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  let line = "";
  let lines = 0;
  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      y += lineHeight;
      line = words[i];
      lines += 1;
      if (lines >= maxLines) {
        ctx.fillText("\u2026", x, y);
        return y + lineHeight;
      }
    } else {
      line = test;
    }
  }
  if (line) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }
  return y;
}

function measurePill(ctx, text, fontSize, padX) {
  ctx.font = `800 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
  return Math.ceil(ctx.measureText(text).width + padX * 2);
}
